import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, CheckCircle, CreditCard, Printer, SealCheck } from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { PortalMessages } from "@/components/PortalMessages";
import { PortalSlabLayout } from "@/components/PortalSlabLayout";
import { PortalColourOptions } from "@/components/PortalColourOptions";
import { SignaturePad } from "@/components/SignaturePad";
import { Countertop3D } from "@/components/Countertop3D";
import { PortalVeinStudio } from "@/components/portal/PortalVeinStudio";
import { QuotePrintout } from "@/components/QuotePrintout";
import { DesignerPlan } from "@/components/CountertopDesigner";
import { EdgeProfilesStrip } from "@/components/EdgeProfilesStrip";
import {
  portalApi,
  portalAsset,
  portalMoney,
  portalDate,
} from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";
import DOMPurify from "dompurify";

const TABS = [
  ["quote", "Quote"],
  ["drawing", "Drawing"],
  ["layout", "Slab layout"],
  ["vein", "Vein match"],
  ["3d", "3D view"],
];

const has3dPieces = (quote) =>
  (quote?.areas || []).some((a) =>
    (a.pieces || []).some((p) => !p.panel_kind && (p.points || []).length > 2),
  );

const visibleTabs = (quote) => {
  const vis = quote?.visibility || {};
  const slabs = quote?.layout?.slabs || [];
  return TABS.filter(([key]) => {
    if (key === "drawing") return vis.show_drawing !== false;
    if (key === "3d")
      return (
        vis.show_3d !== false &&
        quote?.show_3d_in_portal !== false &&
        has3dPieces(quote)
      );
    if (key === "layout")
      return (
        vis.show_layout !== false &&
        quote?.show_layout_in_portal !== false &&
        slabs.length > 0
      );
    if (key === "vein")
      return (
        vis.show_layout !== false &&
        quote?.show_vein_in_portal !== false &&
        has3dPieces(quote)
      );
    return true;
  });
};

const ACTION_TARGET = {
  accept: "portal-accept-card",
  sign: "portal-contract-card",
  drawing: "portal-drawing-approval",
};

// Top-of-page banner: what is still waiting on the customer, with a jump button.
const ActionBanner = ({ quote, onGo }) => {
  const acts = quote.pending_actions || [];
  if (!acts.length) return null;
  const first = acts[0];
  return (
    <div
      data-testid="portal-action-banner"
      className="relative flex flex-col gap-3 overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:p-5"
    >
      <span className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-amber-500" />
      <span className="relative flex h-3 w-3 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-70" />
        <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-600" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-slate-900 md:text-base">
          Your approval is needed on this quote
        </div>
        <div className="mt-0.5 text-xs text-slate-600">
          Still to do:{" "}
          {acts.map((a, i) => (
            <span key={a.key} data-testid={`portal-action-pending-${a.key}`}>
              {i > 0 ? " · " : ""}
              <strong>{a.label}</strong>
            </span>
          ))}
        </div>
      </div>
      <button
        type="button"
        data-testid="portal-action-banner-go"
        onClick={() => onGo(first.key)}
        className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-600 px-5 text-[11px] font-bold uppercase tracking-widest text-white shadow-sm transition-colors hover:bg-amber-700"
      >
        {first.label} <ArrowDown size={14} weight="bold" />
      </button>
    </div>
  );
};

const AcceptCard = ({ quote, onAccepted }) => {
  const [name, setName] = useState(quote.customer_name || "");
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const accepted = quote.acceptance;

  if (quote.visibility?.allow_accept === false && !accepted) return null;

  if (accepted)
    return (
      <div
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
        data-testid="portal-accepted-card"
      >
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle size={18} weight="bold" />
          <span className="text-sm font-semibold">Quote accepted</span>
        </div>
        <p className="mt-2 text-sm text-emerald-700">
          Signed by <strong>{accepted.name}</strong> on{" "}
          {portalDate(accepted.at)}. We are on it — your job will show up under
          Jobs.
        </p>
      </div>
    );

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post(`/quotes/${quote.id}/accept`, {
        signature_name: name.trim(),
        accepted: true,
      });
      toast.success("Thank you — your quote is accepted");
      onAccepted(data.acceptance);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not accept the quote",
      );
    }
    setBusy(false);
  };

  return (
    <div
      className="rounded-2xl border-2 border-amber-400 bg-white p-5 shadow-[0_12px_40px_-16px_rgba(217,119,6,0.45)] sm:p-6"
      data-testid="portal-accept-card"
      id="portal-accept-card"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-800 ring-1 ring-amber-200">
          <SealCheck size={12} weight="bold" /> Step 1 · Awaiting your approval
        </span>
      </div>
      <h2 className="mt-3 text-xl font-bold text-slate-900 sm:text-2xl">
        Approve this quote
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        Tick the box and type your name — that signs the quote and starts your
        job.
      </p>
      <label className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm text-slate-700">
        <input
          data-testid="portal-accept-tick"
          type="checkbox"
          checked={ticked}
          onChange={(e) => setTicked(e.target.checked)}
          className="mt-0.5 h-5 w-5 rounded border-slate-300 accent-amber-600"
        />
        <span>
          I accept this quote
          {quote.totals ? (
            <>
              {" "}
              of <strong>{portalMoney(quote.totals.total)}</strong>
            </>
          ) : null}{" "}
          and the terms shown on it.
        </span>
      </label>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          data-testid="portal-signature-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Type your full name"
          className="h-12 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500"
        />
        <button
          type="button"
          data-testid="portal-accept-button"
          disabled={busy || !ticked || name.trim().length < 2}
          onClick={accept}
          className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 text-[11px] font-bold uppercase tracking-widest text-white shadow-sm transition-colors duration-150 hover:bg-emerald-700 disabled:opacity-40"
        >
          {busy ? (
            "Signing…"
          ) : (
            <>
              <CheckCircle size={15} weight="bold" /> Accept quote
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const DrawingApprovalCard = ({ quote, onChange }) => {
  const [name, setName] = useState(quote.customer_name || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const ap = quote.drawing_approval || {};

  const send = async (path, payload, ok) => {
    setBusy(true);
    try {
      const { data } = await portalApi.post(
        `/quotes/${quote.id}/${path}`,
        payload,
      );
      toast.success(ok);
      onChange(data.drawing_approval);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send that",
      );
    }
    setBusy(false);
  };

  if (ap.status === "approved")
    return (
      <div
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
        data-testid="portal-drawing-approved"
      >
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle size={18} weight="bold" />
          <span className="text-sm font-semibold">Drawing approved</span>
        </div>
        <p className="mt-2 text-sm text-emerald-700">
          Approved by <strong>{ap.name}</strong> on {portalDate(ap.at)}. We will
          cut to this drawing.
        </p>
      </div>
    );

  // Drawing approval removed — the portal no longer gates fabrication on a customer sign-off.
  return null;
};

const printSignedAgreement = (sig) => {
  const w = window.open("", "_blank", "width=820,height=940");
  if (!w) return;
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const paras = DOMPurify.sanitize(sig.body || "");
  w.document.write(
    `<!doctype html><html><head><title>${esc(sig.title || "Agreement")}</title>` +
      `<style>body{font-family:Georgia,'Times New Roman',serif;color:#1e293b;max-width:720px;margin:40px auto;padding:0 28px;line-height:1.55}` +
      `h1{font-size:22px;margin-bottom:20px}h2{font-size:17px;margin:20px 0 8px}p{margin:0 0 12px}` +
      `ul,ol{margin:0 0 12px 22px}li{margin:0 0 6px}hr{border:none;border-top:1px solid #cbd5e1;margin:28px 0}` +
      `.sig img{max-height:90px;display:block;margin:6px 0}.muted{color:#64748b;font-size:13px}</style></head><body>` +
      `<h1>${esc(sig.title || "Agreement")}</h1>${paras}<hr/>` +
      `<div class="sig"><p><strong>Signed by:</strong> ${esc(sig.name)}</p>` +
      (sig.image ? `<img src="${sig.image}" alt="signature"/>` : "") +
      `<p class="muted">Date: ${esc(new Date(sig.at).toLocaleString())}${sig.ip ? ` · IP ${esc(sig.ip)}` : ""}</p></div>` +
      `</body></html>`,
  );
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 350);
};


const ContractCard = ({ quote, onSigned }) => {
  const contract = quote.contract;
  const signed = quote.contract_signature;
  const [name, setName] = useState(quote.customer_name || "");
  const [agree, setAgree] = useState(false);
  const [sigImg, setSigImg] = useState("");
  const [busy, setBusy] = useState(false);

  if (!contract?.enabled) return null;

  if (!quote.acceptance)
    return (
      <div
        className="rounded-2xl border border-slate-200/80 bg-white p-5 text-sm text-slate-500"
        data-testid="portal-contract-locked"
      >
        Accept the quote above to unlock the{" "}
        {contract.title || "agreement"} for signing.
      </div>
    );

  if (signed)
    return (
      <div
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
        data-testid="portal-contract-signed"
      >
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle size={18} weight="bold" />
          <span className="text-sm font-semibold">Agreement signed</span>
        </div>
        <p className="mt-2 text-sm text-emerald-700">
          {signed.title || "Agreement"} signed by <strong>{signed.name}</strong>{" "}
          on {portalDate(signed.at)}.
        </p>
        {signed.image && (
          <img
            src={signed.image}
            alt="Signature"
            data-testid="portal-contract-signature-image"
            className="mt-3 h-16 rounded-lg border border-emerald-200 bg-white p-1"
          />
        )}
        <button
          type="button"
          data-testid="portal-contract-print"
          onClick={() => printSignedAgreement(signed)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-emerald-300 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-emerald-700 transition-colors hover:bg-emerald-100"
        >
          <Printer size={14} weight="bold" /> View / print agreement
        </button>
      </div>
    );

  const sign = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post(
        `/quotes/${quote.id}/sign-contract`,
        { signature_name: name.trim(), signature_image: sigImg, agreed: true },
      );
      toast.success("Thank you — your agreement is signed");
      onSigned(data.contract_signature);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not sign the agreement",
      );
    }
    setBusy(false);
  };

  return (
    <div
      className="rounded-2xl border-2 border-amber-400 bg-white p-5 shadow-[0_12px_40px_-16px_rgba(217,119,6,0.45)] sm:p-6"
      data-testid="portal-contract-card"
      id="portal-contract-card"
    >
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-800 ring-1 ring-amber-200">
        <SealCheck size={12} weight="bold" /> Step 2 · Signature needed
      </span>
      <h3 className="mt-3 text-xl font-bold text-slate-900 sm:text-2xl">
        {contract.title || "Agreement"}
      </h3>
      <p className="mt-1 text-sm text-slate-500">
        Please read this agreement in full, then sign below to authorise
        fabrication.
      </p>
      <div
        className="rte-prose portal-contract-prose mt-4 max-h-72 overflow-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-600"
        data-testid="portal-contract-body"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contract.body || "") }}
      />
      <label className="mt-4 flex items-start gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          data-testid="portal-contract-agree"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          className="mt-0.5 h-5 w-5 accent-teal-600"
        />
        <span>I have read, understood and agree to this agreement.</span>
      </label>
      <div className="mt-4 space-y-2">
        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          Type your full legal name
        </label>
        <input
          data-testid="portal-contract-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className="h-12 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-teal-500"
        />
      </div>
      <div className="mt-4 space-y-2">
        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          Draw your signature
        </label>
        <SignaturePad onChange={setSigImg} dataTestId="portal-signature-pad" clearTestId="portal-signature-clear" />
      </div>
      <button
        type="button"
        data-testid="portal-sign-contract-button"
        disabled={busy || !agree || name.trim().length < 2 || !sigImg}
        onClick={sign}
        className="mt-4 h-12 w-full rounded-xl bg-[#c39e6d] text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#a9855a] disabled:opacity-40"
      >
        {busy ? "Signing…" : "Sign agreement"}
      </button>
    </div>
  );
};

const printDepositSlip = (dep, quote, logoUrl) => {
  const w = window.open("", "_blank", "width=760,height=900");
  if (!w) return;
  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const paidDate = dep.paid_at ? new Date(dep.paid_at).toLocaleString() : "—";
  const row = (label, val) =>
    `<tr><td class="l">${esc(label)}</td><td class="r">${esc(val)}</td></tr>`;
  w.document.write(
    `<!doctype html><html><head><title>Deposit receipt ${esc(dep.code)}</title>` +
      `<style>body{font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;max-width:640px;margin:40px auto;padding:0 28px}` +
      `.shop{font-size:22px;font-weight:700;letter-spacing:.02em}` +
      `.logo{max-height:64px;margin-bottom:12px;display:block}.addr{color:#64748b;font-size:12px;line-height:1.55}` +
      `.tag{display:inline-block;margin-top:6px;padding:3px 10px;border:1px solid #16a34a;border-radius:999px;color:#16a34a;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em}` +
      `h2{font-size:12px;text-transform:uppercase;letter-spacing:.14em;color:#64748b;margin:26px 0 4px}` +
      `table{width:100%;border-collapse:collapse}td{padding:9px 0;border-bottom:1px solid #e2e8f0;font-size:14px}` +
      `td.l{color:#64748b}td.r{text-align:right;font-weight:600}` +
      `tr.big td{border:none;padding-top:16px;font-size:20px;font-weight:700}` +
      `.muted{color:#94a3b8;font-size:12px;margin-top:26px}hr{border:none;border-top:2px solid #0f172a;margin:18px 0}</style></head><body>` +
      `<div class="brand">` +
      (logoUrl ? `<img class="logo" src="${logoUrl}" alt="${esc(dep.shop || "")}"/>` : "") +
      `<div class="shop">${esc(dep.shop || "")}</div>` +
      (dep.shop_address ? `<div class="addr">${esc(dep.shop_address)}</div>` : "") +
      (dep.shop_phone ? `<div class="addr">${esc(dep.shop_phone)}</div>` : "") +
      `</div><span class="tag">Deposit receipt</span><hr/>` +
      `<h2>Receipt</h2><table>` +
      row("Received from", quote.customer_name || "—") +
      row("Project", quote.project_name || "—") +
      row("Invoice", dep.code) +
      (quote.code ? row("Quote", quote.code) : "") +
      row("Date paid", paidDate) +
      `</table><h2>Amounts</h2><table>` +
      row("Deposit total", portalMoney(dep.amount)) +
      row("Amount paid", portalMoney(dep.paid)) +
      `<tr class="big"><td class="l">Balance remaining</td><td class="r">${esc(portalMoney(dep.balance))}</td></tr>` +
      `</table>` +
      `<p class="muted">Thank you for your deposit. This receipt confirms the payment recorded above.</p>` +
      `</body></html>`,
  );
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 350);
};

const DepositCard = ({ quote, logoUrl }) => {
  const dep = quote.deposit;
  const [busy, setBusy] = useState(false);
  if (!dep) return null;
  const contractDone = !quote.contract?.enabled || quote.contract_signature;
  if (!quote.acceptance || !contractDone) return null;

  if (dep.balance <= 0)
    return (
      <div
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
        data-testid="portal-deposit-paid"
      >
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle size={18} weight="bold" />
          <span className="text-sm font-semibold">Deposit paid</span>
        </div>
        <p className="mt-2 text-sm text-emerald-700">
          Thank you — your {portalMoney(dep.amount)} deposit ({dep.code}) is paid
          and your job is scheduled.
        </p>
        <button
          type="button"
          data-testid="portal-print-deposit-slip"
          onClick={() => printDepositSlip(dep, quote, logoUrl)}
          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-300 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-emerald-700 transition-colors duration-150 hover:bg-emerald-100"
        >
          <Printer size={14} weight="bold" /> Print deposit receipt
        </button>
      </div>
    );

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post(
        `/invoices/${dep.invoice_id}/checkout`,
        { origin_url: window.location.origin },
      );
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Card payment is unavailable right now",
      );
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-amber-300 bg-white p-5"
      data-testid="portal-deposit-card"
    >
      <h3 className="text-base font-semibold text-slate-900">Pay your deposit</h3>
      <p className="mt-1 text-sm text-slate-500">
        A {portalMoney(dep.balance)} deposit ({dep.code}) starts your job.
        Fabrication is scheduled once it&apos;s paid.
      </p>
      {dep.can_pay_online ? (
        <button
          type="button"
          data-testid="portal-pay-deposit-button"
          disabled={busy}
          onClick={pay}
          className="mt-4 inline-flex h-12 items-center gap-2 rounded-xl bg-amber-500 px-6 text-[11px] font-bold uppercase tracking-widest text-slate-900 transition-colors duration-150 hover:bg-amber-400 disabled:opacity-40"
        >
          <CreditCard size={16} weight="bold" />
          {busy ? "Opening…" : `Pay ${portalMoney(dep.balance)} deposit online`}
        </button>
      ) : (
        <p className="mt-4 text-sm text-amber-700">
          Please call the shop to pay your deposit.
        </p>
      )}
      {dep.paid > 0 && (
        <button
          type="button"
          data-testid="portal-print-deposit-slip-partial"
          onClick={() => printDepositSlip(dep, quote, logoUrl)}
          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-600 transition-colors duration-150 hover:bg-slate-100"
        >
          <Printer size={14} weight="bold" /> Print deposit receipt
        </button>
      )}
    </div>
  );
};

export default function PortalQuote() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [tab, setTab] = useState("quote");
  const [scene, setScene] = useState(null);
  const [proposal, setProposal] = useState(null);

  const goTo = useCallback(
    (key) => {
      if (key === "drawing") setTab("drawing");
      window.setTimeout(() => {
        document
          .getElementById(ACTION_TARGET[key] || "")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    },
    [],
  );

  const load = useCallback(async () => {
    const { data } = await portalApi.get(`/quotes/${id}`);
    setQuote(data);
    portalApi
      .get(`/quotes/${id}/vein-proposal`)
      .then((r) => setProposal(r.data.proposal || null))
      .catch(() => setProposal(null));
  }, [id]);

  useEffect(() => {
    load();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/branding`)
      .then((r) => r.json())
      .then((d) => setHasLogo(!!d.logo))
      .catch(() => {});
  }, [load]);

  // Arrived from "Needs your approval" on the home screen: jump straight to that step.
  useEffect(() => {
    const action = params.get("action");
    if (!quote || !action) return;
    goTo(action);
    setParams({}, { replace: true });
  }, [quote, params, setParams, goTo]);

  // Recompute what is still pending after each approval so the banner and cards stay in step.
  const patch = (changes) =>
    setQuote((cur) => {
      const next = { ...cur, ...changes };
      const acts = (cur.pending_actions || []).filter((a) => {
        if (a.key === "accept") return !next.acceptance;
        if (a.key === "sign") return !next.contract_signature;
        if (a.key === "drawing") return false;
        return true;
      });
      if (changes.acceptance && cur.contract?.enabled && !next.contract_signature
          && !acts.some((a) => a.key === "sign"))
        acts.unshift({ key: "sign", label: "Sign agreement" });
      return { ...next, pending_actions: acts };
    });

  if (!quote)
    return (
      <PortalShell back="/portal/home">
        <div className="mt-10 h-10 w-10 animate-pulse rounded-full bg-slate-100" />
      </PortalShell>
    );

  const pieces = (quote.areas || []).flatMap((a) => a.pieces || []);

  return (
    <PortalShell
      back="/portal/home"
      title={quote.project_name}
      status={{
        label: quote.status,
        color: {
          Draft: "bg-slate-400",
          Sent: "bg-blue-400",
          Accepted: "bg-emerald-400",
          Declined: "bg-red-400",
          Expired: "bg-amber-400",
        }[quote.status],
      }}
      subtitle={`${quote.code} · ${portalDate(quote.created_at)} · ${
        quote.slab_color || ""
      }`}
    >
      <div
        className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]"
        data-testid="portal-quote-page"
      >
        <div className="min-w-0 space-y-6">
          <ActionBanner quote={quote} onGo={goTo} />

          <PortalColourOptions quoteId={quote.id} closed={!!quote.acceptance} />

          <AcceptCard
            quote={quote}
            onAccepted={(acceptance) => patch({ acceptance })}
          />

          <ContractCard
            quote={quote}
            onSigned={(sig) => patch({ contract_signature: sig })}
          />

          <DepositCard
            quote={quote}
            logoUrl={
              hasLogo
                ? `${process.env.REACT_APP_BACKEND_URL}/api/public/logo`
                : ""
            }
          />

          <div className="no-print flex flex-wrap items-center gap-1 rounded-full border border-slate-200 bg-white p-1.5">
            {visibleTabs(quote).map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`portal-tab-${key}`}
                onClick={() => setTab(key)}
                className={`flex-1 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  tab === key
                    ? "bg-[#c39e6d] text-white"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              data-testid="portal-print-quote"
              onClick={() => window.print()}
              className="flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <Printer size={15} weight="bold" /> Print
            </button>
          </div>

          {tab === "quote" && (
            <div
              className={`overflow-hidden rounded-2xl border ${
                quote.totals
                  ? "border-slate-300 bg-white"
                  : "border-slate-200 bg-white"
              }`}
              data-testid="portal-quote-document"
            >
              {quote.totals ? (
                <QuotePrintout
                  quote={quote}
                  totals={quote.totals}
                  template={quote.template}
                  edgeStyles={quote.edge_styles || []}
                  logoUrl={
                    hasLogo
                      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/logo`
                      : ""
                  }
                  coverUrl={`${process.env.REACT_APP_BACKEND_URL}/api/public/quote-cover/${quote.id}?w=520`}
                  heroUrl={
                    quote.design?.hero
                      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/quote-hero/${quote.id}?w=1400&v=${encodeURIComponent(quote.design.hero.created_at || "")}`
                      : ""
                  }
                />
              ) : (
                <div className="p-6" data-testid="portal-quote-summary">
                  <h2 className="text-base font-semibold text-slate-900 md:text-lg">
                    Quote summary
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Pricing is not shown here — call the shop and we will walk
                    you through it.
                  </p>
                  <dl className="mt-5 divide-y divide-slate-200 text-sm">
                    {[
                      ["Quote", quote.code],
                      ["Material", quote.slab_color],
                      ["Thickness", quote.thickness],
                      ["Square feet", quote.sqft ? `${quote.sqft} ft²` : ""],
                      ["Job address", quote.job_address],
                    ]
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <div
                          key={k}
                          className="flex justify-between gap-4 py-2.5"
                        >
                          <dt className="text-slate-500">{k}</dt>
                          <dd className="font-semibold text-slate-900">{v}</dd>
                        </div>
                      ))}
                  </dl>
                  {quote.notes && (
                    <p className="mt-5 whitespace-pre-wrap text-sm text-slate-500">
                      {quote.notes}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "drawing" && (
            <div
              className="rounded-2xl border border-slate-200/80 bg-white p-5"
              data-testid="portal-drawing"
            >
              <h2 className="text-base font-semibold text-slate-900 md:text-lg">
                Shop drawing
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Every side, edge profile and cut-out we will fabricate.
              </p>
              {(quote.areas || []).map((a) => (
                <div
                  key={a.id}
                  className="mt-6"
                  data-testid={`portal-area-${a.id}`}
                >
                  <div className="text-sm font-semibold text-slate-900">
                    {a.name}
                  </div>
                  <EdgeProfilesStrip
                    pieces={a.pieces || []}
                    testId={`portal-edges-${a.id}`}
                    className="mt-2"
                  />
                  {quote.show_drawings !== false && (
                    <div className="mt-2 border border-slate-200 p-3">
                      <DesignerPlan
                        pieces={a.pieces || []}
                        edgeStyles={quote.edge_styles || []}
                        hideCutoutDims={quote.hide_cutout_dims !== false}
                      />
                    </div>
                  )}
                </div>
              ))}
              {pieces.length === 0 && (
                <p className="mt-4 text-sm text-slate-400">
                  No drawing on this quote yet.
                </p>
              )}
              {pieces.length > 0 && (quote.seams || []).length > 0 && (
                <div
                  className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4"
                  data-testid="portal-seams"
                >
                  <div className="text-sm font-semibold text-slate-900">
                    Seams ({quote.seams.length})
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Where two pieces of stone meet. Seam lengths are shown so you know
                    exactly what to expect.
                  </p>
                  <ul className="mt-3 divide-y divide-amber-100 text-sm">
                    {quote.seams.map((s, i) => (
                      <li
                        key={`${s.a}-${s.b}-${i}`}
                        className="flex items-center justify-between gap-3 py-1.5"
                        data-testid={`portal-seam-${i}`}
                      >
                        <span className="min-w-0 truncate text-slate-700">
                          {s.area} · {s.label}
                        </span>
                        <span className="shrink-0 font-mono text-slate-900">
                          {s.len_text || `${s.len}″`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {false && (
                <div className="mt-6">
                  <DrawingApprovalCard
                    quote={quote}
                    onChange={(drawing_approval) => patch({ drawing_approval })}
                  />
                </div>
              )}
            </div>
          )}

          {tab === "layout" && (
            <div
              className="rounded-2xl border border-slate-200/80 bg-white p-5"
              data-testid="portal-layout"
            >
              <h2 className="text-base font-semibold text-slate-900 md:text-lg">
                Your slab layout
              </h2>
              <p className="mt-1 mb-5 text-sm text-slate-500">
                How your pieces are nested on the slab, so you can see where the
                vein lands.
              </p>
              <PortalSlabLayout quote={quote} />
            </div>
          )}

          {tab === "vein" && (
            <div
              className="rounded-2xl border border-slate-200/80 bg-white p-5"
              data-testid="portal-vein"
            >
              <h2 className="text-base font-semibold text-slate-900 md:text-lg">
                Vein match — arrange your pieces on the slab
              </h2>
              <p className="mt-1 mb-5 text-sm text-slate-500">
                Drag each piece to the part of the slab you'd like it cut from and
                watch the veins run through the seams on the right. Save it and
                your fabricator will see your layout.
              </p>
              <PortalVeinStudio
                quote={quote}
                proposal={proposal?.status === "proposed" ? proposal : null}
                onSaved={setProposal}
              />
            </div>
          )}

          {tab === "3d" && (
            <div
              className="rounded-2xl border border-slate-200/80 bg-white p-5"
              data-testid="portal-3d"
            >
              <h2 className="text-base font-semibold text-slate-900 md:text-lg">
                Your counters in 3D
              </h2>
              <p className="mt-1 mb-5 text-sm text-slate-500">
                Drag to spin around, scroll to zoom. Edge profiles and cut-outs
                are shown as they will be fabricated; the cabinet colors are just
                for the picture.
              </p>
              <Countertop3D
                quote={quote}
                photoOf={(s) => (s.photo ? portalAsset(s.photo) : "")}
                scene={scene || quote.design?.scene}
                onSceneChange={setScene}
                height="55vh"
              />
            </div>
          )}
        </div>

        {quote.visibility?.allow_notes !== false && (
          <aside
            className="no-print lg:sticky lg:top-6"
            data-testid="portal-notes-panel"
          >
            <PortalMessages subjectType="quote" subjectId={quote.id} />
          </aside>
        )}
      </div>
    </PortalShell>
  );
}
